import { i18n } from '@lingui/core';
import type { SortKey } from '@schibsted-nmp/search-frontend-clients/quest';
import { toast } from '@warp-ds/elements/toast';
import { Select } from '@warp-ds/react';
import React from 'react';
import { useSearchData } from '../../data/SearchData';
import { useSubmitSearch } from '../../data/SearchQuery';
import { useSortData } from '../../data/SortData';
import { APP_URL_PARAMS } from '../../state/UrlState';

const FIVE_SECONDS_IN_MILLISECONDS = 5000;
const SORT_BY_GEO_LOCATION_VALUE = 'CLOSEST';

type SortingProps = {
    className?: string;
};

export function Sorting(props: SortingProps) {
    const { className = '' } = props;

    const { submitSearch } = useSubmitSearch();

    const selectSorting = React.useCallback(
        async (ev: React.ChangeEvent) => {
            const value = (ev.target as HTMLSelectElement).value as SortKey;

            let coords: Coordinates;
            if (value === SORT_BY_GEO_LOCATION_VALUE) {
                try {
                    coords = await getCurrentPosition();
                } catch (err) {
                    toast(
                        i18n.t({
                            id: 'sorting.closest.failed',
                            message: 'Sort by "Closest" failed',
                        }),
                        {
                            type: 'error',
                        },
                    );
                    return;
                }
            }

            submitSearch((urlParams) => {
                urlParams.set(APP_URL_PARAMS.sort, value);
                if (coords) {
                    urlParams.set(APP_URL_PARAMS.lat, coords.lat);
                    urlParams.set(APP_URL_PARAMS.lon, coords.lon);
                }
            });
        },
        [submitSearch],
    );

    const { searchData } = useSearchData();
    const { sortData } = useSortData();

    if (!sortData) {
        return null;
    }

    return (
        <Select
            aria-label={i18n.t({
                id: 'sorting.label',
                message: 'Sort by',
            })}
            className={`flex items-baseline space-x-8 ${className}`}
            onChange={selectSorting}
            value={searchData?.metadata.sort}
        >
            {sortData.map((option) => (
                <option key={option.sort} value={option.sort}>
                    {option.description}
                </option>
            ))}
        </Select>
    );
}

type Coordinates = {
    lat: string;
    lon: string;
};

async function getCurrentPosition(): Promise<Coordinates> {
    if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser');
    }

    const position = (await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: FIVE_SECONDS_IN_MILLISECONDS,
        });
    })) as GeolocationPosition;

    // 5 digits is precise down to ~1.11 meters: https://en.wikipedia.org/wiki/Decimal_degrees
    return {
        lat: position.coords.latitude.toFixed(5),
        lon: position.coords.longitude.toFixed(5),
    };
}
